import React, { useState, useEffect } from 'react';
import { ExternalLink, Image as ImageIcon, FileText } from 'lucide-react';

const LinkPreview = ({ url }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPreview = async () => {
      if (!url) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Call the API directly for link preview
        const response = await fetch(`https://api.bigclipboard.com/link-preview?url=${encodeURIComponent(url)}`);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Link preview error response:', errorText);
          throw new Error(`Failed to fetch preview: ${response.status}`);
        }
        
        const data = await response.json();
        setPreview(data);
      } catch (err) {
        console.error('Error fetching link preview:', err);
        setError('Failed to load preview');
      } finally {
        setLoading(false);
      }
    };

    fetchPreview();
  }, [url]);

  if (loading) {
    return (
      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 animate-pulse">
        <div className="flex items-center space-x-3">
          <div className="w-16 h-16 bg-gray-200 rounded"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !preview) {
    return (
      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
        <div className="flex items-center space-x-2 text-gray-600">
          <ExternalLink className="h-4 w-4" />
          <span className="text-sm truncate">{url}</span>
        </div>
      </div>
    );
  }

  return (
    <a 
      href={url} 
      target="_blank" 
      rel="noopener noreferrer"
      className="block border border-gray-200 rounded-lg overflow-hidden bg-white hover:shadow-md hover:border-gray-300 transition-all duration-200 cursor-pointer"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div className="flex">
        {preview.image && (
          <div className="flex-shrink-0 w-24 h-24">
            <img 
              src={preview.image} 
              alt={preview.title || 'Link preview'}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
              onLoad={(e) => {
                // Image loaded successfully
              }}
            />
          </div>
        )}
        <div className="flex-1 p-3 min-w-0">
          <h3 className="font-medium text-gray-900 text-sm line-clamp-2 mb-1">
            {preview.title || 'No title available'}
          </h3>
          {preview.description && (
            <p className="text-gray-600 text-xs line-clamp-2 mb-2">
              {preview.description}
            </p>
          )}
          <div className="flex items-center space-x-1 text-gray-500 text-xs">
            <ExternalLink className="h-3 w-3" />
            <span className="truncate">{new URL(url).hostname}</span>
          </div>
        </div>
      </div>
    </a>
  );
};

export default LinkPreview; 